import { useState, useEffect, useCallback } from 'react';
import useCanvasStore from '@/store/canvasStore';
import api from '@/lib/api';
import { Plus } from 'lucide-react';

/**
 * GhostSuggestions - AI co-thinking component
 * Shows subtle AI-suggested branches as floating chips in a column
 */
export default function GhostSuggestions({ nodeId, nodeLabel, nodeDescription }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  const mode = useCanvasStore(s => s.mode);
  const nodes = useCanvasStore(s => s.nodes);
  const edges = useCanvasStore(s => s.edges);
  const addNode = useCanvasStore(s => s.addNode);
  const updateNodeData = useCanvasStore(s => s.updateNodeData);

  // Get context for better suggestions
  const getContext = useCallback(() => {
    const parentEdge = edges.find(e => e.target === nodeId);
    const parentNode = parentEdge ? nodes.find(n => n.id === parentEdge.source) : null;
    
    const siblingLabels = [];
    if (parentEdge) {
      const siblingEdges = edges.filter(e => e.source === parentEdge.source && e.target !== nodeId);
      siblingEdges.forEach(e => {
        const sibling = nodes.find(n => n.id === e.target);
        if (sibling) siblingLabels.push(sibling.data.label);
      });
    }
    
    const childEdges = edges.filter(e => e.source === nodeId);
    childEdges.forEach(e => {
      const child = nodes.find(n => n.id === e.target);
      if (child) siblingLabels.push(child.data.label);
    });

    return {
      parentLabel: parentNode?.data?.label || '',
      siblingLabels
    };
  }, [nodeId, nodes, edges]);

  // Fetch suggestions
  useEffect(() => {
    if (mode !== 'edit' || dismissed) {
      setSuggestions([]);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const context = getContext();
        const res = await api.post('/ai/suggest-branches', {
          nodeLabel,
          nodeDescription: nodeDescription || '',
          parentLabel: context.parentLabel,
          siblingLabels: context.siblingLabels
        });
        
        if (res.data.suggestions && res.data.suggestions.length > 0) {
          setSuggestions(res.data.suggestions.slice(0, 4)); // Max 4 suggestions
          setShowSuggestions(true);
        }
      } catch (err) {
        console.log('Suggestions unavailable');
      } finally {
        setLoading(false);
      }
    }, 300); // Reduced delay for faster response

    return () => clearTimeout(timer);
  }, [nodeId, nodeLabel, nodeDescription, mode, dismissed, getContext]);

  // Reset on node change
  useEffect(() => {
    setDismissed(false);
    setSuggestions([]);
    setShowSuggestions(false);
  }, [nodeId]);

  const handleAddSuggestion = useCallback((e, suggestion) => {
    e.stopPropagation();
    e.preventDefault();
    
    addNode(nodeId, suggestion.label, 'child');
    
    setTimeout(() => {
      const state = useCanvasStore.getState();
      const newNode = state.nodes.find(n => n.data.label === suggestion.label);
      if (newNode && suggestion.description) {
        updateNodeData(newNode.id, { description: suggestion.description });
      }
    }, 50);
    
    setSuggestions(prev => prev.filter(s => s.label !== suggestion.label));
  }, [nodeId, addNode, updateNodeData]);

  if (mode !== 'edit' || !showSuggestions || suggestions.length === 0) {
    return null;
  }

  return (
    <div 
      className="absolute z-50 pointer-events-auto" 
      style={{ 
        top: '100%', 
        left: '50%', 
        transform: 'translateX(-50%)',
        marginTop: '40px'
      }}
      onClick={e => e.stopPropagation()}
      onMouseDown={e => e.stopPropagation()}
    >
      {/* Minimal connector */}
      <div className="absolute left-1/2 -top-[32px] -translate-x-1/2">
        <div className="w-px h-6 bg-gradient-to-b from-transparent to-gray-300/50" />
        <div className="w-1 h-1 rounded-full bg-gray-300 -ml-[1px]" />
      </div>

      {/* Suggestion chips - vertical column */}
      <div 
        className="flex flex-col items-center gap-1"
        onMouseDown={e => e.stopPropagation()}
      >
        {suggestions.map((suggestion, idx) => (
          <button
            key={idx}
            onClick={(e) => handleAddSuggestion(e, suggestion)}
            onMouseDown={e => e.stopPropagation()}
            className="group flex items-center gap-1 px-2 py-0.5
                       bg-white/70 backdrop-blur-sm
                       border border-gray-200/50 hover:border-gray-300
                       rounded-full shadow-sm hover:shadow
                       transition-all duration-150 ease-out
                       hover:-translate-y-px hover:bg-white/90"
            title={suggestion.description}
            style={{ 
              opacity: 0,
              animation: `fadeIn 0.2s ease-out ${idx * 40}ms forwards`
            }}
          >
            <Plus 
              className="w-2.5 h-2.5 text-gray-400 group-hover:text-cobalt transition-colors" 
              strokeWidth={2.5} 
            />
            <span className="text-[10px] font-body text-gray-500 group-hover:text-gray-800 transition-colors whitespace-nowrap">
              {suggestion.label}
            </span>
          </button>
        ))}
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
